import type { TotalPromotion } from '@commerce/core'
import type { Story } from '../demo'

/**
 * The reels are storefront-wide promotions now, from `getTotalPromotions` —
 * not the `demo/` fixtures. This maps one promotion onto the `Story` the reel
 * components already render, so the viewer and the rail stay unchanged.
 */

/** The design's soft tints, rotated so consecutive reels do not repeat. */
const TINTS = ['#E7F3EA', '#EEE9F7', '#FBEFDD', '#E2F1F7'] as const

/**
 * The big glyph a reel shows — the discount, when the promotion names one.
 *
 * A `TOTAL` promotion like "VIP 15%" or "Bulk 20%" carries its number in the
 * name or the description; pulling it out gives the tile the design's bold
 * "15%" rather than a truncated sentence. When there is no number to find, the
 * promotion's initials stand in — never an empty tile.
 */
export const promotionBadge = (promotion: TotalPromotion): string => {
  const haystack = `${promotion.name ?? ''} ${promotion.description ?? ''}`
  const percent = haystack.match(/(\d{1,3})\s*%/)
  if (percent) return `${percent[1]}%`
  const combo = haystack.match(/(\d+\s*[x×]\s*\d+)/i)
  if (combo) return combo[1]!.replace(/\s+/g, '').replace('x', '×')

  const words = (promotion.name ?? promotion.code ?? '').trim().split(/\s+/).filter(Boolean)
  const initials = words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join('')
  return initials || '%'
}

/** A promotion as the reel rail and the story viewer want it. */
export const storyFromPromotion = (promotion: TotalPromotion, index: number): Story => {
  const badge = promotionBadge(promotion)
  const name = promotion.name ?? promotion.code ?? 'Promoción'
  return {
    key: promotion.id,
    label: name,
    initials: badge,
    tint: TINTS[index % TINTS.length]!,
    eyebrow: 'Promoción de la semana',
    title: badge,
    body: promotion.description ?? name,
    ctaLabel: 'Ver productos',
    action: 'browse',
  }
}

/**
 * The active promotions as reels, in the API's priority order (lower first),
 * with inactive ones dropped. An empty result means no reels — the caller
 * hides the rail and the "Promos" button rather than drawing an empty rail.
 */
export const storiesFromPromotions = (
  promotions: readonly TotalPromotion[],
): Story[] =>
  [...promotions]
    .filter((promotion) => promotion.isActive !== false)
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map(storyFromPromotion)
